import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { ReferenceDataHttpService, ReferenceDatum, ReferenceDataTypes } from '@sondermind/data-access/reference-data';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';

import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';

import { IAPIFetchResponse, IAPIListResponse } from '@sondermind/utilities/models-http';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

import {
  ProviderSpecialtiesService,
  ISpecialtyDatum
} from '@sondermind/data-access/provider-facing/provider-profile';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';
import { RefDatumOrder } from '@sondermind/core/design/reference-datum-checklist/reference-datum-checklist.component';

@Component({
  selector: 'member-profile-specialties',
  templateUrl: './profile-specialties.component.html',
  styleUrls: ['./profile-specialties.component.scss', '../../modules/profile/profile-app-shared-styles.scss'],
  standalone: false
})
export class ProfileSpecialtiesPage implements OnInit, OnDestroy, IComponentCanDeactivate {
  user: ProviderUser = null;

  topForm: UntypedFormGroup = null;
  topFormReset: any = null;
  checkedForm: UntypedFormGroup = null;
  checkedFormReset: any = null;
  topSpecialtySelected: boolean = false;

  saving = false;
  loaded = false;

  private destroyed$ = new Subject<void>();

  userSpecialtyPrefs$ = this.specialtiesService.index({ page: 'disabled' });

  readonly specialties$ = this.refDataService
    .list(
      ReferenceDataTypes.specialties,
      {
        page: 'disabled',
        sort: { key: 'title', asc: true },
        filter: { key: 'hidden', value: 'false' }
      }
    )
    .pipe(map((s) => s.data));

  readonly specialties = toSignal(this.specialties$, { initialValue: [] as ReferenceDatum[] });
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  RefDatumOrder = RefDatumOrder;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private appService: SondermindApplicationService,
    private specialtiesService: ProviderSpecialtiesService,
    private refDataService: ReferenceDataHttpService,
    private snackBar: MatSnackBar
  ) {
    this.checkedForm = formBuilder.group({});
    this.topForm = formBuilder.group({
      topSpecialty1: new UntypedFormControl(null),
      topSpecialty2: new UntypedFormControl(null),
      topSpecialty3: new UntypedFormControl(null)
    });
  }

  ngOnInit(): void {
    const user$ = this.appService.currentUser$.pipe(filter((u) => !!u));
    // eslint-disable-next-line no-return-assign
    user$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((u) => {
      this.user = u;
    });

    combineLatest([this.specialties$, this.userSpecialtyPrefs$]).pipe(
      takeUntil(this.destroyed$)
    ).subscribe({
      next: ([spec, uspec]) => {
        this.processInitialResponse(spec, uspec);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });

    this.topForm.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((values) => {
      let selected = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const val in values) {
        if (values[val]) selected = true;
      }
      this.topSpecialtySelected = selected;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.checkedForm?.pristine && this.topForm?.pristine;
  }

  onCancel(): void {
    this.topForm.reset(this.topFormReset);
    this.checkedForm.reset(this.checkedFormReset);
  }

  onSave(): void {
    if (this.saving || !this.topSpecialtySelected) {
      this.snackBar.open('Error saving. Please check required fields on Specialties.', 'Dismiss', { duration: 30000, panelClass: 'toast-color' });
      return;
    }

    this.saving = true;

    const topIds = Object.values(this.topForm.getRawValue())
      .filter((v) => !!v)
      .map((v) => parseInt(v.toString(), 10))
      .filter((v) => v > 0);

    const checkedIds = Object
      .keys(this.checkedForm.controls)
      .filter((key) => this.checkedForm.controls[key].value);

    this.specialtiesService
      .update(this.user.id, { specialty_ids: checkedIds, top_specialty_ids: topIds })
      .subscribe({
        next: (r) => {
          this.checkedForm.markAsPristine();
          this.topForm.markAsPristine();
          this.processUpdateResponse(r);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  private processInitialResponse(refSpecs: ReferenceDatum[], userSpecs: IAPIListResponse<ISpecialtyDatum>) {
    const { data } = userSpecs;
    this.initializeCheckedSpecialties(refSpecs, data);
    this.setTopSpecialties(data);

    this.topFormReset = this.topForm.value;
    this.checkedFormReset = this.checkedForm.value;

    this.loaded = true;
  }

  private processUpdateResponse(resp: IAPIFetchResponse<ISpecialtyDatum[]>) {
    const { data } = resp;
    this.setCheckedSpecialties(data);
    this.setTopSpecialties(data);

    this.topFormReset = this.topForm.value;
    this.checkedFormReset = this.checkedForm.value;

    this.checkedForm.markAsPristine();
    this.topForm.markAsPristine();

    // kick off reload so we get new profile submission errors
    this.appService.reloadCurrentUser();

    this.snackBar.open('Your specialties have been saved.', 'Dismiss');
    this.saving = false;
  }

  private setTopSpecialties(data: ISpecialtyDatum[]) {
    this.topSpecialtySelected = !!data.length;
    this.topForm.reset();
    data
      .filter((mspec) => mspec.top_specialty)
      .forEach((mspec, i) => {
        this.topForm.get(`topSpecialty${mspec.priority_order + 1}`).setValue(mspec.reference_data_id);
        this.checkedForm.get(mspec.reference_data_id.toString()).setValue(true);
      });
  }

  private initializeCheckedSpecialties(refSpecs: ReferenceDatum[], data: ISpecialtyDatum[]) {
    refSpecs.forEach((spec, i) => {
      const ctrl = new UntypedFormControl(this.isChecked(data, spec));
      this.checkedForm.addControl(String(spec.id), ctrl);
    });
  }

  private setCheckedSpecialties(data: ISpecialtyDatum[]) {
    this.specialties().forEach((spec) => {
      const ctrl = this.checkedForm.get(String(spec.id));
      ctrl?.setValue(this.isChecked(data, spec));
    });
  }

  private isChecked(mspecs: ISpecialtyDatum[], spec: ReferenceDatum): boolean {
    return mspecs.some((mspec) => mspec.reference_data_id === spec.id);
  }
}
